import {
    Add as AddIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Error as ErrorIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    UploadFile as UploadFileIcon
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { defaultLocale } from '../../constants/locale';
import { useFormatter } from '../../context/FormatterContext';
import ResponsiveTooltip from '../ResponsiveTooltip';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Platform } from '../../services/integration.types';
import { Integration } from '../../services/mocks/data/integrations';
import { useUserIntegrationsService } from '../../services/user-integrations.service';
import DeleteIntegrationConfirmModal from './DeleteIntegrationConfirmModal';
import IntegrationExchangeCsvFormComponent from './integration-forms/ExchangeCsv';
import IntegrationAssetsModal from './IntegrationAssetsModal';
import IntegrationSelectModal from './IntegrationSelectModal';

interface IntegrationsOverviewProps {
    integrations: Integration[] | undefined;
    onSync: (integration: Integration) => void;
    onDelete: (integration: Integration) => void;
    deletingIntegrationName: string | null;
    isLoading: boolean;
}

const StatusChip: React.FC<{ 
    status: Integration['status'],
    onClick?: () => void 
}> = ({ status, onClick }) => {
    const { t } = useTranslation();
    const getStatusConfig = (status: Integration['status']['status']) => {
        switch (status) {
            case 'in_progress':
                return {
                    label: t('integrations.overview.status.syncing'),
                    bgColor: '#ffca27',
                    textColor: '#000000',
                    icon: <RefreshIcon fontSize="small" sx={{ 
                        animation: 'spin 2s linear infinite',
                        '@keyframes spin': {
                            '0%': {
                                transform: 'rotate(0deg)',
                            },
                            '100%': {
                                transform: 'rotate(360deg)',
                            },
                        },
                        marginRight: '4px',
                        fontSize: '0.875rem',
                    }} />,
                };
            case 'done':
                return {
                    label: t('integrations.overview.status.synced'),
                    bgColor: '#15B834',
                    textColor: '#FFFFFF',
                    icon: <CheckCircleIcon />,
                };
            case 'error':
                return {
                    label: t('integrations.overview.status.error'),
                    bgColor: '#D32F2F',
                    textColor: '#FFFFFF',
                    icon: <ErrorIcon />,
                };
        }
    };

    const config = getStatusConfig(status.status);
    
    // Determine if the chip should be clickable (only when status is error)
    const isClickable = status.status === 'error' && onClick;
    
    return (
        <ResponsiveTooltip title={status.message} arrow placement="top">
            <Typography 
                variant="body2" 
                onClick={(e) => {
                    if (isClickable) {
                        e.stopPropagation();
                        onClick?.();
                    }
                }}
                sx={{ 
                    color: config.textColor,
                    fontWeight: 'bold',
                    bgcolor: config.bgColor,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: isClickable ? 'pointer' : 'default',
                    '&:hover': isClickable ? {
                        opacity: 0.9,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                    } : {},
                    '& svg': {
                        mr: 0.5,
                        fontSize: '1rem',
                    }
                }}
            >
                {config.icon}
                {config.label}
            </Typography>
        </ResponsiveTooltip>
    );
};

const IntegrationsOverview: React.FC<IntegrationsOverviewProps> = ({
    integrations,
    onSync,
    onDelete,
    deletingIntegrationName,
    isLoading,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { showNotification } = useNotification();
    const { formatCurrency } = useFormatter();
    const userIntegrationsService = useUserIntegrationsService();
    const queryClient = useQueryClient();
    
    // State for edit mode
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuIntegration, setMenuIntegration] = useState<Integration | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editIntegration, setEditIntegration] = useState<Integration | null>(null);
    const [editImportType, setEditImportType] = useState<string>('api');
    const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
    const [csvUploadPlatform, setCsvUploadPlatform] = useState<Platform | null>(null);

    // Use React Query for data fetching
    const queryFn = async () => {
        try {
            const platforms = await userIntegrationsService.getUserIntegrations();
            console.log('Fetched user integrations with React Query:', platforms);
            return platforms;
        } catch (error) {
            console.error('Error fetching user integrations:', error);
            return [];
        }
    };

    const { 
        data: userPlatforms = [], 
        isLoading: isLoadingUserIntegrations, 
        refetch: refetchUserIntegrations 
    } = useQuery({
        queryKey: ['userIntegrations'],
        queryFn,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 60000 // Cache for 1 minute
    });

        // Check for selected integrations that are already integrated and update their status
    useEffect(() => {
        if (integrations && userPlatforms) {
            // Find platforms with 'selected' status that are already integrated
            const selectedPlatforms = userPlatforms.filter(platform => platform.status === 'selected');
            
            if (selectedPlatforms.length > 0) {
                // Check each selected platform against the integrations list
                selectedPlatforms.forEach(platform => {
                    // Check if this platform is already integrated
                    const isAlreadyIntegrated = integrations.some(integration => 
                        integration.platformType == platform.platform_type && integration.platformName == platform.name
                    );
                    
                    if (isAlreadyIntegrated) {
                        // Update the status to 'integrated'
                        console.log(`Platform ${platform.name} is already integrated, updating status...`);
                        userIntegrationsService.updateIntegrationStatus(platform.name, platform.platform_type)
                            .then(() => {
                                console.log(`Updated integration status for ${platform.name} to 'integrated'`);
                            })
                            .catch(error => {
                                console.error(`Error updating integration status for ${platform.name}:`, error);
                            });
                    }
                });
            }
        }
    }, [integrations, userPlatforms]);


    // Filter platforms that have not been removed
    const userPlatformsFiltered = userPlatforms.filter(
    platform => platform.status !== 'removed' && platform.status !== undefined &&!(integrations || []).some(integration => 
                    integration.platformType == platform.platform_type && integration.platformName == platform.name
    ));

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, integration: Integration) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuIntegration(integration);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuExited = () => {
        setMenuIntegration(null);
    };

    const handleSync = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            onSync(menuIntegration);
        }
        handleMenuClose();
    };

    const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setIntegrationToDelete(menuIntegration);
            setDeleteConfirmOpen(true);
        }
        handleMenuClose();
    };

    const handleViewAssets = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setSelectedIntegration(menuIntegration);
        }
        handleMenuClose();
    };

    const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        handleMenuClose();
        
        if (menuIntegration) {
            setEditIntegration(menuIntegration);
            setEditImportType('api');
            setIsAddModalOpen(true);
        }
    };

    const handleImportCSV = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setEditIntegration(menuIntegration);
            setIsAddModalOpen(true);
            setEditImportType('csv');
        }
        handleMenuClose();
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditIntegration(null);
        setEditImportType('api');
    };

    const handleConfirmDelete = () => {
        if (integrationToDelete) {
            onDelete(integrationToDelete);
            setDeleteConfirmOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setIntegrationToDelete(null);
    };

    const SkeletonRow = () => (
        <TableRow>
            <TableCell><Skeleton variant="text" width={120} /></TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton variant="text" width={80} /></TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton variant="text" width={100} /></TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><Skeleton variant="text" width={100} /></TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><Skeleton variant="text" width={100} /></TableCell>
            <TableCell><Skeleton variant="text" width={100} /></TableCell>
            <TableCell><Skeleton variant="text" width={100} /></TableCell>
        </TableRow>
    );

    const welcomeBoxStyles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3'
    };

    return (
        <>
            {/* Welcome Box */}
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                justifyContent: 'space-between',
                mb: 3,
                gap: 2
            }}>
                {/* Left side - Welcome box or empty space */}
                <Box sx={{ 
                    flexGrow: 1, 
                    mr: { xs: 0, sm: 2 },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { sm: 'calc(100% - 180px)' }
                }}>
                    {/* Only show the welcome box if there are integrations */}
                    {userPlatformsFiltered.length > 0 && (
                        <Alert
                            severity="info"
                            icon={false}
                            sx={{
                                backgroundColor: welcomeBoxStyles.backgroundColor,
                                color: welcomeBoxStyles.textPrimary,
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                border: `1px solid ${welcomeBoxStyles.border}`,
                                borderRadius: 1.5,
                                p: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                position: 'relative'
                            }}
                        >
                            {/* Edit Mode Toggle Button */}
                            <IconButton 
                                size="small" 
                                onClick={() => setIsEditMode(!isEditMode)}
                                sx={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    color: welcomeBoxStyles.textPrimary,
                                    backgroundColor: isEditMode ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    }
                                }}
                                aria-label={isEditMode ? t('common.done') : t('common.edit')}
                            >
                                {isEditMode ? <CheckCircleIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                            </IconButton>
                            
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                width: '100%',
                                gap: 1.5
                            }}>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        fontWeight: 'bold', 
                                        fontSize: '1.1rem',
                                        color: welcomeBoxStyles.textPrimary
                                    }}
                                >
                                    🏁 {t('integrations.overview.welcomeMessage')}
                                </Typography>
                                
                                {/* Platform chips moved below text */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 1, 
                                    flexWrap: 'wrap',
                                    maxWidth: '100%',
                                    overflow: 'hidden'
                                }}>
                                    {(isLoading || isLoadingUserIntegrations) ? (
                                        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                                            {[1, 2, 3].map((item) => (
                                                <Skeleton 
                                                    key={item} 
                                                    variant="rounded" 
                                                    width={120} 
                                                    height={32} 
                                                    sx={{ borderRadius: '16px', flexShrink: 0 }} 
                                                />
                                            ))}
                                        </Stack>
                                    ) : userPlatforms.length > 0 && (
                                        <Stack 
                                            direction="row" 
                                            spacing={1} 
                                            sx={{ 
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                maxWidth: '100%',
                                                '&::-webkit-scrollbar': {
                                                    height: '4px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: alpha(theme.palette.primary.main, 0.1),
                                                    borderRadius: '2px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: alpha(theme.palette.primary.main, 0.3),
                                                    borderRadius: '2px',
                                                },
                                            }}
                                        >
                                            {/* Sort platforms to show blockchain integrations first */}
                                            {userPlatformsFiltered
                                                .sort((a, b) => {
                                                    // First sort by platform_type (blockchain first)
                                                    if (a.platform_type === 'blockchain' && b.platform_type !== 'blockchain') return -1;
                                                    if (a.platform_type !== 'blockchain' && b.platform_type === 'blockchain') return 1;
                                                    
                                                    // Then sort by name
                                                    return a.display_name.localeCompare(b.display_name);
                                                })
                                                .map((platform, index) => (
                                                <Box
                                                    key={`${platform.name}-${index}-${platform.display_name}`}
                                                    onClick={() => {
                                                        // Different behavior based on edit mode
                                                        if (isEditMode) {
                                                            // In edit mode, mark as removed
                                                            userIntegrationsService.removeIntegration(platform.name, platform.platform_type)
                                                                .then(() => {
                                                                    showNotification(
                                                                        t('integrations.overview.notifications.removed', { name: platform.display_name })
                                                                    );
                                                                    // Refresh the list
                                                                    refetchUserIntegrations();
                                                                    queryClient.invalidateQueries({ queryKey: ['userIntegrations'] });
                                                                })
                                                                .catch((error) => {
                                                                    console.error('Error removing integration:', error);
                                                                    showNotification(
                                                                        t('integrations.overview.notifications.removeError')
                                                                    );
                                                                });
                                                        } else {
                                                            // In normal mode, open the integration
                                                            // Check if this is a custom integration
                                                            const isCustom = platform.name.startsWith('custom-') || platform.isCustom;
                                                            
                                                            console.log('Clicked on platform:', platform);
                                                            
                                                            if (isCustom) {
                                                                // For custom integrations, directly open the file upload modal
                                                                const fileUploadPlatform = {
                                                                    name: 'excelUpload',
                                                                    display_name: platform.display_name,
                                                                    platform_type: 'excelUpload',
                                                                    logo_uri: '',
                                                                    legacy: false,
                                                                    position: 0,
                                                                    beta: false,
                                                                    custom_api_uri: '',
                                                                    custom_csv_uri: '',
                                                                };
                                                                
                                                                // Set up for CSV upload
                                                                setCsvUploadPlatform(fileUploadPlatform);
                                                                setShowCsvUploadModal(true);
                                                                
                                                                console.log('Opening file upload for custom integration:', platform.name);
                                                            } else {
                                                                // For standard integrations, use the regular API integration
                                                                setEditIntegration({
                                                                    name: platform.name,
                                                                    platformName: platform.name,
                                                                    jobId: platform.name,
                                                                    jobTableName: '',
                                                                    status: { status: 'done', message: '' },
                                                                    importTypes: ['api'] as any,
                                                                    assets: []
                                                                } as any);
                                                                setEditImportType('api');
                                                                setIsAddModalOpen(true);
                                                                
                                                                console.log('Opening API integration for standard platform:', platform.name);
                                                            }
                                                        }
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        padding: '4px 10px',
                                                        borderRadius: '4px',
                                                        backgroundColor: 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: (theme) => theme.palette.divider,
                                                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                                                        transition: 'all 0.2s ease',
                                                        cursor: 'pointer',
                                                        flexShrink: 0,
                                                        '&:hover': {
                                                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                                                            backgroundColor: (theme) => 
                                                                theme.palette.mode === 'dark' 
                                                                    ? alpha(theme.palette.background.paper, 0.7) 
                                                                    : alpha(theme.palette.primary.light, 0.05),
                                                            borderColor: (theme) => 
                                                                theme.palette.mode === 'dark'
                                                                    ? 'rgba(255, 255, 255, 0.5)'
                                                                    : theme.palette.primary.main
                                                        }
                                                    }}
                                                >
                                                    {platform.logo_uri ? (
                                                        <Box
                                                            component="img"
                                                            src={'https://cointracking.info/' + platform.logo_uri}
                                                            alt={platform.display_name}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                objectFit: 'contain',
                                                                backgroundColor: isDarkMode ? '#121212' : 'transparent'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                backgroundColor: (theme) => theme.palette.primary.main,
                                                                color: 'white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '12px',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {platform.display_name.charAt(0).toUpperCase()}
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {platform.display_name}
                                                    </Typography>
                                                    
                                                    {/* X icon for removing in edit mode */}
                                                    {isEditMode && (
                                                        <Box
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // Handle removal when clicking the X icon
                                                                userIntegrationsService.removeIntegration(platform.name, platform.platform_type)
                                                                    .then(() => {
                                                                        showNotification(
                                                                            t('integrations.overview.notifications.removed', { name: platform.display_name })
                                                                        );
                                                                        // Refresh the list
                                                                        refetchUserIntegrations();
                                                                        queryClient.invalidateQueries({ queryKey: ['userIntegrations'] });
                                                                    })
                                                                    .catch((error) => {
                                                                        console.error('Error removing integration:', error);
                                                                        showNotification(
                                                                            t('integrations.overview.notifications.removeError')
                                                                        );
                                                                    });
                                                            }}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: 16,
                                                                height: 16,
                                                                borderRadius: '50%',
                                                                backgroundColor: (theme) => alpha(theme.palette.divider, 0.5),
                                                                color: 'text.secondary',
                                                                flexShrink: 0,
                                                                fontSize: '14px',
                                                                '&:hover': {
                                                                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                                                                    color: 'error.main',
                                                                }
                                                            }}
                                                        >
                                                            ×
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            </Box>
                        </Alert>
                    )}
                </Box>
                
                {/* Right side - Add Integration button */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-end', 
                    mt: { xs: 2, sm: 0 },
                    alignSelf: { xs: 'center', sm: 'flex-end' },
                    minWidth: '170px',
                    justifyContent: { xs: 'center', sm: 'flex-end' },
                    height: { xs: 'auto', sm: userPlatformsFiltered.length > 0 ? '100%' : 'auto' }
                }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        sx={{ 
                            whiteSpace: 'nowrap',
                            minWidth: '170px'
                        }}
                    >
                        {t('integrations.overview.addButton')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    px: { xs: 1, sm: 2, md: 4 },
                    py: 1.5,
                    overflowX: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.divider, 0.6),
                        borderRadius: 3,
                    },
                    '& .MuiTableBody-root tr:last-child td': {
                        borderBottom: '0 !important',
                    },
                    '& .MuiTableBody-root tr:last-child th': {
                        borderBottom: '0 !important',
                    },
                }}
            >
                <Table sx={{
                    '& .MuiTableCell-root': {
                        py: 2.5,
                        borderBottom: 'none', 
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        px: { xs: 1, sm: 2 },
                    },
                    '& thead .MuiTableCell-root': {
                        borderBottom: '2px solid',
                        borderBottomColor: `${theme.palette.divider}`,
                        borderBottomOpacity: 0.6,
                        py: 2,
                    },
                    '& tbody .MuiTableRow-root:not(:last-child) .MuiTableCell-root': {
                        borderBottom: '2px solid',
                        borderBottomColor: `${theme.palette.divider}`,
                        borderBottomOpacity: 0.6,
                    },
                    '& .MuiTypography-root': {
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    },
                    tableLayout: { xs: 'auto', md: 'fixed' },
                    borderCollapse: 'collapse',
                    width: '100%',
                    minWidth: {
                        xs: '650px', 
                        sm: '850px',
                        md: '100%',
                    },
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.account')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.transactions')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.assets')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.value')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.lastSync')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.status')}</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('integrations.overview.table.actions')}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <>
                                <SkeletonRow key="skeleton-1" />
                                <SkeletonRow key="skeleton-2" />
                                <SkeletonRow key="skeleton-3" />
                            </>
                        ) : integrations === undefined || integrations === null || integrations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="body1" sx={{ p: 3, color: theme.palette.text.secondary }}>
                                        {t('integrations.overview.noIntegrations')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                        <>
                            {integrations.map((integration) => (
                                <TableRow
                                    key={integration.name + integration.jobId}
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover
                                        },
                                    }}
                                    onClick={() => {
                                        if (!anchorEl) {
                                            setSelectedIntegration(integration);
                                        }
                                    }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Box sx={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2
                                        }}>
                                            <Avatar
                                                src={integration.logoUri ? 'https://cointracking.info/' + integration.logoUri : ''}
                                                alt={integration.name}
                                                sx={{ 
                                                    width: 36,
                                                    height: 36
                                                }}
                                            />
                                            <Box>
                                                {integration.walletAddress ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography 
                                                            variant="body2" 
                                                            sx={{ 
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {integration.name}
                                                        </Typography>
                                                        <ResponsiveTooltip 
                                                            title={integration.walletAddress} 
                                                            arrow 
                                                            placement="top"
                                                        >
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (integration.walletAddress) {
                                                                        navigator.clipboard.writeText(integration.walletAddress);
                                                                        showNotification(t('common.copied'), 'success');
                                                                    }
                                                                }}
                                                                sx={{ 
                                                                    ml: 0.5, 
                                                                    p: 0.5,
                                                                    color: theme.palette.text.secondary,
                                                                    '&:hover': {
                                                                        color: theme.palette.primary.main
                                                                    }
                                                                }}
                                                            >
                                                                <ContentCopyIcon fontSize="small" sx={{ fontSize: '0.85rem' }} />
                                                            </IconButton>
                                                        </ResponsiveTooltip>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {integration.name}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{integration.transactionsCount}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{integration.assetsCount}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatCurrency(integration.value)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {formatDistanceToNow(new Date(integration.endTime * 1000), {
                                                addSuffix: true,
                                                locale: defaultLocale
                                            })}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <StatusChip 
                                            status={integration.status} 
                                            onClick={integration.status.status === 'error' ? 
                                                () => {
                                                    // Set the current integration as the one to edit
                                                    setMenuIntegration(integration);
                                                    setEditIntegration(integration);
                                                    setEditImportType('api');
                                                    setIsAddModalOpen(true);
                                                } : undefined
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton 
                                            onClick={(event) => handleMenuClick(event, integration)} 
                                            size="small"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                }
                                            }}
                                        >
                                            {deletingIntegrationName === integration.name ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <MoreVertIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl)}
                                            onClose={handleMenuClose}
                                            TransitionProps={{
                                                onExited: handleMenuExited
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                            slotProps={{
                                                paper: {
                                                    sx: {
                                                        width: 200,
                                                        maxWidth: '100%',
                                                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                                                        mt: 0.5,
                                                        '& .MuiList-root': {
                                                            py: 0.5,
                                                        }
                                                    }
                                                }
                                            }}
                                            MenuListProps={{
                                                dense: true,
                                                onClick: (e) => e.stopPropagation()
                                            }}
                                            sx={{
                                                '& .MuiMenuItem-root': {
                                                    py: 1,
                                                    px: 2,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                    }
                                                },
                                                '& .MuiListItemIcon-root': {
                                                    minWidth: 32,
                                                    color: theme.palette.text.secondary,
                                                },
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                }
                                            }}
                                        >
                                            {menuIntegration?.jobId && menuIntegration?.status?.status !== 'error' && (
                                                <MenuItem onClick={handleSync} disabled={!!deletingIntegrationName}>
                                                    <ListItemIcon>
                                                        <RefreshIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={t('integrations.overview.actions.sync')} />
                                                </MenuItem>
                                            )}
                                            {menuIntegration?.platformType === 'exchange' && menuIntegration?.jobId && (
                                                <MenuItem onClick={handleEdit} disabled={!!deletingIntegrationName}>
                                                    <ListItemIcon>
                                                        <EditIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={t('integrations.overview.actions.edit')} />
                                                </MenuItem>
                                            )}
                                            {menuIntegration?.importTypes &&
                                                menuIntegration.importTypes.includes('csv') && (
                                                    <MenuItem
                                                        onClick={handleImportCSV}
                                                        disabled={!!deletingIntegrationName}
                                                    >
                                                        <ListItemIcon>
                                                            <UploadFileIcon fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={t('integrations.overview.actions.importCsv')} />
                                                    </MenuItem>
                                                )}
                                            <MenuItem onClick={handleViewAssets} disabled={!!deletingIntegrationName}>
                                                <ListItemIcon>
                                                    <AssessmentIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={t('integrations.overview.actions.viewAssets')} />
                                            </MenuItem>
                                            <MenuItem
                                                onClick={handleDelete}
                                                disabled={!!deletingIntegrationName || menuIntegration?.status?.status === 'in_progress'}
                                                sx={{ 
                                                    color: theme.palette.error.main,
                                                    '& .MuiListItemIcon-root': {
                                                        color: theme.palette.error.main
                                                    }
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <DeleteIcon fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={t('integrations.overview.actions.delete')} />
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </>
                    )}
                    </TableBody>
                </Table>
            </TableContainer>

            <IntegrationSelectModal
                open={isAddModalOpen}
                onClose={handleCloseAddModal}
                selectedPlatformName={editIntegration?.platformName}
                selectedImportType={editImportType}
            />

            {selectedIntegration && (
                <IntegrationAssetsModal
                    open={!!selectedIntegration}
                    onClose={() => setSelectedIntegration(null)}
                    integration={selectedIntegration}
                />
            )}

            <DeleteIntegrationConfirmModal
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                integration={integrationToDelete}
                isDeleting={!!deletingIntegrationName && integrationToDelete?.name === deletingIntegrationName}
            />

            {/* Direct CSV Upload Modal for Custom Integrations */}
            {showCsvUploadModal && csvUploadPlatform && (
                <Dialog
                    open={showCsvUploadModal}
                    onClose={() => setShowCsvUploadModal(false)}
                    maxWidth="lg"
                    fullWidth
                    BackdropProps={{
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)'
                        }
                    }}
                    PaperProps={{
                        sx: { 
                            width: '80%',
                            maxWidth: '1000px',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            bgcolor: theme => theme.palette.background.paper,
                            '& .MuiDialogContent-root': {
                                bgcolor: theme => theme.palette.background.paper,
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{ bgcolor: theme => theme.palette.background.paper }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1 
                        }}>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                {csvUploadPlatform.display_name}
                            </Typography>
                            <IconButton 
                                aria-label="close" 
                                onClick={() => setShowCsvUploadModal(false)} 
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <IntegrationExchangeCsvFormComponent
                                platform={csvUploadPlatform}
                                onJobCreated={() => {
                                    setShowCsvUploadModal(false);
                                    if (onSync && editIntegration) onSync(editIntegration);
                                }}
                                isOnboarding={false}
                                importer={{
                                    id: 0,
                                    customText: '',
                                    displayName: 'File Upload',
                                    position: 0,
                                    type: 'excel',
                                    uri: '/import/import_xls',
                                    uploadFile: '',
                                    importFolder: '',
                                    customFields: [],
                                }}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default IntegrationsOverview;
